/**
 * Territorios del Clan — Servicios (Mocks, opción A hex-grid)
 * Este mock genera un pequeño clúster de hexes alrededor del origen para visualizar en HexMap.
 * Usa el idOrSlug real del clan para colorear celdas controladas consistentemente.
 *
 * Biomas: bosque, montaña, río, desierto, urbano, sagrado
 * Rendimientos base (aprobado):
 *  - bosque: +2 Ryo/h, +2 materiales/día
 *  - montaña: +3 Ryo/h, +1 material/día
 *  - río: +1 Ryo/h, +3 materiales/día
 *  - desierto: +1 Ryo/h, +1 material/día
 *  - urbano: +4 Ryo/h, 0 materiales/día
 *  - sagrado: +2 Ryo/h, +2 materiales/día, +5% bonus de drop en misiones
 * Cap diario por clan (mock no aplica cap): 500 Ryo, 40 materiales
 */

const BIOME_YIELDS = {
  bosque: { ryoPerHour: 2, matsPerDay: 2 },
  montaña: { ryoPerHour: 3, matsPerDay: 1 },
  río: { ryoPerHour: 1, matsPerDay: 3 },
  desierto: { ryoPerHour: 1, matsPerDay: 1 },
  urbano: { ryoPerHour: 4, matsPerDay: 0 },
  sagrado: { ryoPerHour: 2, matsPerDay: 2, dropBonus: 0.05 },
};

/**
 * @typedef {Object} TerritoryCell
 * @property {string|number} territoryId
 * @property {string} territorySlug
 * @property {string} territoryName
 * @property {number} q
 * @property {number} r
 * @property {'Neutral'|'Paz'|'Tension'|'Guerra'|'Ocupado'|'Disputa'} status
 * @property {string} [since] ISO
 * @property {string|number} [clanId]
 * @property {string} [clanSlug]
 * @property {string} [clanName]
 * @property {'bosque'|'montaña'|'río'|'desierto'|'urbano'|'sagrado'} biome
 */

/**
 * Mock: Overview de territorios por clan (cells + resumen estimado)
 * @param {string|number} clanIdOrSlug
 * @returns {Promise<{ clanIdOrSlug: string|number, cells: TerritoryCell[], summary: { controlledCount:number, disputedCount:number, upkeepPerDayRyo:number, estRyoPerDay:number, estMaterialsPerDay:number }, updatedAt:string }>}
 */
export async function fetchClanTerritoriesOverview(clanIdOrSlug) {
  const now = new Date();
  const isoNow = now.toISOString();

  // Clúster axial simple alrededor de (0,0)
  // Controladas por el clan: centro y anillo parcial
  const CLAN = String(clanIdOrSlug);
  const cells = [
    { q: 0, r: 0, biome: 'urbano', status: 'Paz' },
    { q: 1, r: 0, biome: 'bosque', status: 'Paz' },
    { q: 1, r: -1, biome: 'montaña', status: 'Paz' },
    { q: 0, r: -1, biome: 'río', status: 'Disputa' }, // disputado
    { q: -1, r: 0, biome: 'desierto', status: 'Neutral' },
    { q: -1, r: 1, biome: 'bosque', status: 'Neutral' },
    { q: 0, r: 1, biome: 'sagrado', status: 'Paz' },
  ].map((c, i) => {
    const id = `hex-${i + 1}`;
    const name = `Hex ${c.q},${c.r}`;
    const base = {
      territoryId: id,
      territorySlug: id,
      territoryName: name,
      q: c.q,
      r: c.r,
      status: c.status,
      since: isoNow,
      biome: c.biome,
    };
    if (c.status === 'Paz' || c.status === 'Disputa') {
      return {
        ...base,
        clanId: CLAN,
        clanSlug: CLAN,
        clanName: `Clan ${CLAN}`,
      };
    }
    return base;
  });

  // Resumen estimado (solo celdas controladas 'Paz')
  let estRyoPerDay = 0;
  let estMaterialsPerDay = 0;
  let controlledCount = 0;
  let disputedCount = 0;
  for (const c of cells) {
    if (c.status === 'Paz' && c.clanSlug === CLAN) {
      controlledCount += 1;
      const y = BIOME_YIELDS[c.biome] || { ryoPerHour: 0, matsPerDay: 0 };
      estRyoPerDay += y.ryoPerHour * 24;
      estMaterialsPerDay += y.matsPerDay;
    } else if (c.status === 'Disputa') {
      disputedCount += 1;
    }
  }

  // Mantenimiento: 50 Ryo/hex/día por cada hex controlado + disputado (a criterio)
  const upkeepPerDayRyo = (controlledCount + disputedCount) * 50;

  return {
    clanIdOrSlug,
    cells,
    summary: {
      controlledCount,
      disputedCount,
      upkeepPerDayRyo,
      estRyoPerDay,
      estMaterialsPerDay,
    },
    updatedAt: isoNow,
  };
}